'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Star, MapPin, Search, Heart, Sparkles } from 'lucide-react';
import Link from 'next/link';
import CosmicBackground from '@/components/CosmicBackground';
import BottomNav from '@/components/BottomNav';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ConnectedProfile {
  id: string;
  name: string;
  age: number;
  location: string;
  avatar: string;
  sunSign: string;
  moonSign: string;
  nakshatra: string;
  gunaMilanScore: number;
  lastMessage: string;
  lastMessageTime: string;
  isOnline: boolean;
  unreadCount: number;
  element: 'Fire' | 'Earth' | 'Air' | 'Water';
}

interface PresenceState {
  [userId: string]: {
    online: boolean;
    typing: boolean;
    lastSeen?: string;
  };
}

const DUMMY_CONNECTIONS: ConnectedProfile[] = [
{
  id: 'c-001',
  name: 'Priya Sharma',
  age: 27,
  location: 'Mumbai, India',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4984a58-1774186470833.png",
  sunSign: 'Cancer',
  moonSign: 'Pisces',
  nakshatra: 'Rohini',
  gunaMilanScore: 32,
  lastMessage: 'Our moon signs are in perfect trine 🌙 I felt the connection instantly.',
  lastMessageTime: '2m ago',
  isOnline: true,
  unreadCount: 3,
  element: 'Water'
},
{
  id: 'c-002',
  name: 'Ananya Verma',
  age: 25,
  location: 'Delhi, India',
  avatar: "https://images.unsplash.com/flagged/photo-1569575104460-de6ecdf626fc",
  sunSign: 'Taurus',
  moonSign: 'Virgo',
  nakshatra: 'Uttara Phalguni',
  gunaMilanScore: 29,
  lastMessage: 'Have you read about the Venus transit this week? It\'s affecting us both!',
  lastMessageTime: '1h ago',
  isOnline: true,
  unreadCount: 1,
  element: 'Earth'
},
{
  id: 'c-003',
  name: 'Kavya Nair',
  age: 28,
  location: 'Bangalore, India',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18d67842e-1766583414857.png",
  sunSign: 'Leo',
  moonSign: 'Aries',
  nakshatra: 'Magha',
  gunaMilanScore: 26,
  lastMessage: 'That meditation session you recommended was incredible ✨',
  lastMessageTime: '3h ago',
  isOnline: false,
  unreadCount: 0,
  element: 'Fire'
},
{
  id: 'c-004',
  name: 'Meera Iyer',
  age: 26,
  location: 'Chennai, India',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff205bbb-1772343451789.png",
  sunSign: 'Libra',
  moonSign: 'Gemini',
  nakshatra: 'Swati',
  gunaMilanScore: 24,
  lastMessage: 'Let\'s compare our birth charts sometime! I\'m curious about our Bhakoot.',
  lastMessageTime: 'Yesterday',
  isOnline: false,
  unreadCount: 0,
  element: 'Air'
},
{
  id: 'c-005',
  name: 'Riya Kapoor',
  age: 29,
  location: 'Pune, India',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17d6560e3-1772259711205.png",
  sunSign: 'Scorpio',
  moonSign: 'Cancer',
  nakshatra: 'Anuradha',
  gunaMilanScore: 31,
  lastMessage: 'The stars aligned for us — 31 Gunas is rare 💫',
  lastMessageTime: 'Yesterday',
  isOnline: true,
  unreadCount: 2,
  element: 'Water'
},
{
  id: 'c-006',
  name: 'Divya Menon',
  age: 24,
  location: 'Hyderabad, India',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1caa20a54-1776752146310.png",
  sunSign: 'Sagittarius',
  moonSign: 'Leo',
  nakshatra: 'Moola',
  gunaMilanScore: 22,
  lastMessage: 'Your Jupiter placement is so interesting! Mine is in the 9th house too.',
  lastMessageTime: '2 days ago',
  isOnline: false,
  unreadCount: 0,
  element: 'Fire'
}];


const ELEMENT_STYLES: Record<string, {badge: string;glow: string;icon: string;}> = {
  Fire: { badge: 'badge-fire', glow: 'rgba(239,68,68,0.2)', icon: '🔥' },
  Earth: { badge: 'badge-earth', glow: 'rgba(132,204,22,0.2)', icon: '🌿' },
  Air: { badge: 'badge-air', glow: 'rgba(96,165,250,0.2)', icon: '💨' },
  Water: { badge: 'badge-water', glow: 'rgba(6,182,212,0.2)', icon: '💧' }
};

function getScoreColor(score: number): string {
  if (score >= 28) return '#22c55e';
  if (score >= 18) return '#FBBF24';
  return '#f97316';
}

function getScoreLabel(score: number): string {
  if (score >= 28) return 'Excellent';
  if (score >= 18) return 'Good';
  return 'Average';
}

// Typing dots animation component
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-0.5 ml-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-1 h-1 rounded-full bg-accent inline-block"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        />
      ))}
    </span>
  );
}

export default function ConnectionsContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'online'>('all');
  const [presenceState, setPresenceState] = useState<PresenceState>({});
  const [channelReady, setChannelReady] = useState(false);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const typingTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const { user } = useAuth();

  // Simulate typing for demo connections (since dummy data has no real Supabase users)
  const simulateTyping = useCallback(() => {
    const onlineIds = DUMMY_CONNECTIONS
      .filter((c) => c.isOnline)
      .map((c) => c.id);

    if (onlineIds.length === 0) return;

    const randomId = onlineIds[Math.floor(Math.random() * onlineIds.length)];

    setPresenceState((prev) => ({
      ...prev,
      [randomId]: { ...prev[randomId], online: true, typing: true },
    }));

    // Clear typing after 2–4 seconds
    const duration = 2000 + Math.random() * 2000;
    if (typingTimersRef.current[randomId]) {
      clearTimeout(typingTimersRef.current[randomId]);
    }
    typingTimersRef.current[randomId] = setTimeout(() => {
      setPresenceState((prev) => ({
        ...prev,
        [randomId]: { ...prev[randomId], typing: false },
      }));
    }, duration);
  }, []);

  useEffect(() => {
    const supabase = createClient();

    // Initialize presence state from dummy data
    const initial: PresenceState = {};
    DUMMY_CONNECTIONS.forEach((c) => {
      initial[c.id] = { online: c.isOnline, typing: false };
    });
    setPresenceState(initial);

    // Join the connections presence channel
    const channel = supabase.channel('connections-presence', {
      config: { presence: { key: user?.id ?? 'anonymous' } },
    });

    channelRef.current = channel;

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<{ userId: string; typing?: boolean }>();
        setPresenceState((prev) => {
          const updated = { ...prev };
          // Mark all real Supabase users as online if they appear in presence state
          Object.values(state).forEach((presences) => {
            presences.forEach((p) => {
              if (p.userId && updated[p.userId] !== undefined) {
                updated[p.userId] = {
                  ...updated[p.userId],
                  online: true,
                  typing: p.typing ?? false,
                };
              }
            });
          });
          return updated;
        });
        setChannelReady(true);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        newPresences.forEach((p: any) => {
          if (p.userId) {
            setPresenceState((prev) => ({
              ...prev,
              [p.userId]: { online: true, typing: p.typing ?? false },
            }));
          }
        });
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        leftPresences.forEach((p: any) => {
          if (p.userId) {
            setPresenceState((prev) => ({
              ...prev,
              [p.userId]: { online: false, typing: false, lastSeen: new Date().toISOString() },
            }));
          }
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && user?.id) {
          await channel.track({ userId: user.id, typing: false });
          setChannelReady(true);
        }
      });

    // Simulate typing activity for demo (since dummy profiles aren't real Supabase users)
    const typingInterval = setInterval(simulateTyping, 6000 + Math.random() * 4000);

    return () => {
      clearInterval(typingInterval);
      Object.values(typingTimersRef.current).forEach(clearTimeout);
      supabase.removeChannel(channel);
    };
  }, [user?.id, simulateTyping]);

  const filtered = DUMMY_CONNECTIONS.filter((c) => {
    const liveOnline = presenceState[c.id]?.online ?? c.isOnline;
    const matchesSearch =
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.sunSign.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filter === 'all' ||
      (filter === 'unread' && c.unreadCount > 0) ||
      (filter === 'online' && liveOnline);
    return matchesSearch && matchesFilter;
  });

  const totalUnread = DUMMY_CONNECTIONS.reduce((sum, c) => sum + c.unreadCount, 0);
  const onlineCount = DUMMY_CONNECTIONS.filter((c) => presenceState[c.id]?.online ?? c.isOnline).length;

  return (
    <div className="min-h-screen nebula-bg stars-bg relative">
      <CosmicBackground />

      {/* Header */}
      <div className="sticky top-0 z-40 glass-strong border-b border-purple-mid/20 px-4 pt-12 pb-3">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-silver flex items-center gap-2">
                <Heart size={20} className="text-accent" />
                Connections
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                {DUMMY_CONNECTIONS.length} cosmic matches
                {totalUnread > 0 &&
                  <span className="px-1.5 py-0.5 rounded-full bg-accent/20 text-accent text-[10px] font-semibold">
                    {totalUnread} new
                  </span>
                }
                {/* Live online count */}
                <span className="flex items-center gap-1 text-[10px] text-green-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
                  {onlineCount} online
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-accent animate-pulse" />
              <span className="text-xs text-muted-foreground">Sorted by Guna Milan</span>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-surface border border-purple-mid/30 text-sm text-silver placeholder:text-muted-foreground focus:outline-none focus:border-purple-light/60 transition-colors" />
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2">
            {(['all', 'unread', 'online'] as const).map((f) =>
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all duration-200 ${
                  filter === f ?
                  'bg-purple-mid/40 text-accent border border-accent/30' : 'bg-surface/60 text-muted-foreground border border-purple-mid/20 hover:text-silver'}`
                }>
                {f}
                {f === 'unread' && totalUnread > 0 &&
                  <span className="ml-1 text-accent">({totalUnread})</span>
                }
                {f === 'online' &&
                  <span className="ml-1 text-green-400">({onlineCount})</span>
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Connection List */}
      <div className="max-w-lg mx-auto px-4 py-4 pb-28 space-y-3">
        {filtered.length === 0 ?
          <div className="text-center py-16 text-muted-foreground">
            <Star size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No connections found</p>
          </div> :

          filtered.map((profile, index) => {
            const scoreColor = getScoreColor(profile.gunaMilanScore);
            const scoreLabel = getScoreLabel(profile.gunaMilanScore);
            const elemStyle = ELEMENT_STYLES[profile.element];
            const scorePercent = profile.gunaMilanScore / 36 * 100;

            // Live presence data (falls back to dummy data)
            const liveOnline = presenceState[profile.id]?.online ?? profile.isOnline;
            const isTyping = presenceState[profile.id]?.typing ?? false;

            return (
              <motion.div
                key={profile.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06, duration: 0.35, ease: 'easeOut' }}
                className="glass-card rounded-2xl p-4 card-hover relative overflow-hidden"
                style={{ borderColor: `${scoreColor}22` }}>

                {/* Subtle score glow */}
                <div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{ background: `radial-gradient(ellipse at top right, ${scoreColor}08 0%, transparent 60%)` }} />

                <div className="flex items-start gap-3 relative">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div
                      className="w-16 h-16 rounded-2xl overflow-hidden border-2"
                      style={{ borderColor: liveOnline ? `${scoreColor}70` : `${scoreColor}30` }}>
                      <Image
                        src={profile.avatar}
                        alt={`${profile.name}, ${profile.sunSign}, ${profile.age} years old`}
                        width={64}
                        height={64}
                        className="w-full h-full object-cover" />
                    </div>

                    {/* Online indicator — animated pulse when online */}
                    <AnimatePresence>
                      {liveOnline && (
                        <motion.span
                          key="online-dot"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-green-400 border-2 border-card flex items-center justify-center">
                          <span className="w-2 h-2 rounded-full bg-green-300 animate-ping absolute opacity-75" />
                        </motion.span>
                      )}
                    </AnimatePresence>

                    {profile.unreadCount > 0 &&
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-accent text-[10px] font-bold text-midnight flex items-center justify-center px-1">
                        {profile.unreadCount}
                      </span>
                    }
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-silver text-sm leading-tight flex items-center gap-1.5">
                          {profile.name}, {profile.age}
                          {/* Live online label */}
                          {liveOnline && (
                            <span className="text-[9px] font-medium text-green-400 bg-green-400/10 border border-green-400/20 px-1.5 py-0.5 rounded-full leading-none">
                              Online
                            </span>
                          )}
                        </h3>
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin size={10} className="text-muted-foreground flex-shrink-0" />
                          <span className="text-[11px] text-muted-foreground truncate">{profile.location}</span>
                        </div>
                      </div>

                      {/* Guna Milan Score Badge */}
                      <div
                        className="flex-shrink-0 flex flex-col items-center px-2.5 py-1.5 rounded-xl border"
                        style={{ borderColor: `${scoreColor}40`, background: `${scoreColor}12` }}>
                        <span className="text-base font-bold leading-none" style={{ color: scoreColor }}>
                          {profile.gunaMilanScore}
                        </span>
                        <span className="text-[9px] text-muted-foreground leading-tight">/ 36</span>
                        <span className="text-[9px] font-medium mt-0.5" style={{ color: scoreColor }}>
                          {scoreLabel}
                        </span>
                      </div>
                    </div>

                    {/* Astro tags */}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-deep/40 text-purple-glow border border-purple-mid/30">
                        ☀️ {profile.sunSign}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-deep/40 text-purple-glow border border-purple-mid/30">
                        🌙 {profile.moonSign}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${elemStyle.badge}`}>
                        {elemStyle.icon} {profile.element}
                      </span>
                    </div>

                    {/* Score bar */}
                    <div className="mt-2.5">
                      <div className="h-1 w-full rounded-full bg-surface overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: scoreColor }}
                          initial={{ width: 0 }}
                          animate={{ width: `${scorePercent}%` }}
                          transition={{ delay: index * 0.06 + 0.2, duration: 0.6, ease: 'easeOut' }} />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Nakshatra: <span className="text-silver/70">{profile.nakshatra}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Last message preview / typing indicator */}
                <div className="mt-3 flex items-end justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <AnimatePresence mode="wait">
                      {isTyping ? (
                        <motion.div
                          key="typing"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.2 }}
                          className="flex items-center gap-1">
                          <span className="text-[11px] text-accent/80 italic">typing</span>
                          <TypingDots />
                        </motion.div>
                      ) : (
                        <motion.p
                          key="message"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.2 }}
                          className="text-[11px] text-muted-foreground truncate leading-relaxed">
                          {profile.lastMessage}
                        </motion.p>
                      )}
                    </AnimatePresence>
                    <span className="text-[10px] text-muted-foreground/60 mt-0.5 block">
                      {isTyping ? (
                        <span className="text-accent/60">Active now</span>
                      ) : (
                        profile.lastMessageTime
                      )}
                    </span>
                  </div>

                  {/* Quick Message Button */}
                  <Link
                    href="/cosmic-chat"
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl btn-cosmic text-xs font-medium text-silver transition-all duration-200 hover:scale-105 active:scale-95">
                    <MessageCircle size={13} />
                    <span>Message</span>
                  </Link>
                </div>
              </motion.div>
            );
          })
        }
      </div>

      <BottomNav />
    </div>
  );
}
